import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { getProductImageUrl } from '../utils/productImage';

const C = { primary: '#8B1A1A', gold: '#D4A017' };
const JUTTI_SIZES = ['36', '37', '38', '39', '40', '41', '42'];
const FREE_SHIPPING_THRESHOLD = 999;

const isJutti = (product) => (
  (product.category_slug || product.category?.slug || '').toLowerCase() === 'juttis'
);

export default function Cart() {
  const navigate = useNavigate();
  const { items, subtotal, shipping, total, dispatch } = useCart();
  const { isMobile } = useBreakpoint();
  const missingSizeItem = items.find(item => isJutti(item.product) && !item.size);

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <div style={{ fontSize: 60, marginBottom: 20 }}>🛒</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: '#2C2C2C', marginBottom: 12 }}>Your Cart is Empty</h2>
        <p style={{ color: '#888', marginBottom: 32, fontSize: 16 }}>Add some beautiful pieces to your collection.</p>
        <button onClick={() => navigate('/shop')} style={{ background: C.primary, color: '#fff', border: 'none', padding: '13px 32px', borderRadius: 4, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          Continue Shopping
        </button>
      </div>
    );
  }

  const toFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const shippingProgress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);

  const ShippingProgress = () => (
    <div style={{ background: subtotal >= FREE_SHIPPING_THRESHOLD ? '#e8f5e9' : '#FFF8F0', border: `1px solid ${subtotal >= FREE_SHIPPING_THRESHOLD ? '#a5d6a7' : '#f0d8b8'}`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
      {subtotal >= FREE_SHIPPING_THRESHOLD ? (
        <p style={{ fontSize: 13, color: '#2e7d32', fontWeight: 700, margin: 0, textAlign: 'center' }}>
          🎉 You've unlocked Free Express Shipping!
        </p>
      ) : (
        <>
          <p style={{ fontSize: 13, color: '#555', margin: '0 0 8px' }}>
            Add <strong style={{ color: C.primary }}>₹{Math.round(toFreeShipping).toLocaleString('en-IN')}</strong> more for <strong>FREE Express Shipping!</strong>
          </p>
          <div style={{ background: '#e0d0d0', borderRadius: 999, height: 7, overflow: 'hidden' }}>
            <div style={{ width: `${shippingProgress}%`, background: C.primary, borderRadius: 999, height: '100%', transition: 'width 0.4s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 10, color: '#aaa' }}>₹0</span>
            <span style={{ fontSize: 10, color: '#aaa' }}>₹{FREE_SHIPPING_THRESHOLD}</span>
          </div>
        </>
      )}
    </div>
  );

  const OrderSummary = () => (
    <div style={{ background: '#fff', borderRadius: 8, padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, marginBottom: 16 }}>Order Summary</h2>
      <ShippingProgress />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 14 }}>
        <span style={{ color: '#666' }}>Subtotal</span>
        <span>₹{Math.round(subtotal).toLocaleString('en-IN')}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 14 }}>
        <span style={{ color: '#666' }}>Shipping</span>
        <span style={{ color: shipping === 0 ? '#2e7d32' : '#2C2C2C' }}>
          {shipping === 0 ? 'Free' : `₹${shipping}`}
        </span>
      </div>
      <div style={{ borderTop: '1.5px solid #f0e0e0', paddingTop: 16, display: 'flex', justifyContent: 'space-between', marginBottom: 20, fontWeight: 700, fontSize: 18 }}>
        <span>Total</span>
        <span style={{ color: C.primary }}>₹{Math.round(total).toLocaleString('en-IN')}</span>
      </div>
      {missingSizeItem && (
        <p style={{ color: '#c62828', fontSize: 12, marginBottom: 12 }}>
          Select a size for {missingSizeItem.product.name} before checkout.
        </p>
      )}
      <button
        onClick={() => navigate('/checkout')}
        disabled={Boolean(missingSizeItem)}
        style={{ width: '100%', padding: '14px', background: missingSizeItem ? '#ccc' : C.primary, color: '#fff', border: 'none', borderRadius: 4, fontSize: 15, fontWeight: 700, cursor: missingSizeItem ? 'not-allowed' : 'pointer' }}
      >
        Proceed to Checkout
      </button>
      <button onClick={() => navigate('/shop')} style={{ width: '100%', padding: '12px', background: 'none', color: C.primary, border: `1.5px solid ${C.primary}`, borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 10 }}>
        Continue Shopping
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 24px' }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 32, color: '#2C2C2C', marginBottom: 24 }}>Your Cart</h1>

      {/* On mobile, show summary first (above items) */}
      {isMobile && (
        <div style={{ marginBottom: 24 }}>
          <OrderSummary />
        </div>
      )}

      {/* Multi-item promo banner */}
      {totalQty === 1 && (
        <div style={{ background: '#fffde7', border: '1px solid #f9e07a', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 20 }}>🎁</span>
          <p style={{ fontSize: 13, color: '#6d4c00', margin: 0, flex: 1 }}>
            <strong>Add 1 more item</strong> to unlock our <strong>Buy 2, Get a Free Earring Pair</strong> offer!
          </p>
          <Link to="/shop?category=earrings" style={{ fontSize: 13, color: C.primary, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Browse Earrings →
          </Link>
        </div>
      )}
      {totalQty >= 2 && (
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 20 }}>🎉</span>
          <p style={{ fontSize: 13, color: '#1b5e20', margin: 0, flex: 1 }}>
            <strong>You qualify!</strong> Add a free earring pair — use code <strong>FREEPAIR</strong> at checkout.
          </p>
          <Link to="/shop?category=earrings" style={{ fontSize: 13, color: '#2e7d32', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Choose Earrings →
          </Link>
        </div>
      )}

      <div style={{ display: 'flex', gap: 32, flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {items.map(item => {
            const imageUrl = getProductImageUrl(
              item.product,
              `https://placehold.co/100x120/8B1A1A/gold?text=${encodeURIComponent(item.product.name.slice(0, 10))}`
            );
            const unitPrice = Math.round(parseFloat(item.product.effective_price || item.product.price));

            return (
              <div key={item.id} style={{ display: 'flex', gap: 14, padding: '16px 0', borderBottom: '1px solid #f0e0e0', alignItems: 'center' }}>
                <img
                  src={imageUrl}
                  alt={item.product.name}
                  style={{ width: isMobile ? 72 : 90, height: isMobile ? 88 : 110, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                  onError={e => { e.target.src = 'https://placehold.co/100x120/8B1A1A/gold?text=A1'; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, color: '#999', marginBottom: 2 }}>{item.product.category_name}</p>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 14 : 16, color: '#2C2C2C', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.product.name}</h3>
                  {isJutti(item.product) ? (
                    <label style={{ display: 'block', fontSize: 12, color: item.size ? '#666' : '#c62828', marginBottom: 6 }}>
                      Size:{' '}
                      <select
                        value={item.size || ''}
                        onChange={event => dispatch({ type: 'UPDATE_SIZE', payload: { id: item.id, size: event.target.value } })}
                        aria-label={`Size for ${item.product.name}`}
                        style={{ marginLeft: 4, padding: '3px 6px', border: `1px solid ${item.size ? '#d8c8c8' : '#c62828'}`, borderRadius: 3, background: '#fff', color: '#333' }}
                      >
                        <option value="">Select size</option>
                        {JUTTI_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </label>
                  ) : item.size ? (
                    <p style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Size: {item.size}</p>
                  ) : null}
                  <p style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>₹{unitPrice.toLocaleString('en-IN')}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #e0d0d0', borderRadius: 4 }}>
                    <button
                      onClick={() => item.quantity > 1
                        ? dispatch({ type: 'UPDATE_QUANTITY', payload: { id: item.id, quantity: item.quantity - 1 } })
                        : dispatch({ type: 'REMOVE_ITEM', payload: item.id })}
                      style={{ width: 30, height: 34, border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: C.primary }}
                    >−</button>
                    <span style={{ width: 24, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{item.quantity}</span>
                    <button
                      onClick={() => dispatch({ type: 'UPDATE_QUANTITY', payload: { id: item.id, quantity: item.quantity + 1 } })}
                      style={{ width: 30, height: 34, border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: C.primary }}
                    >+</button>
                  </div>
                  <p style={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C' }}>
                    ₹{(unitPrice * item.quantity).toLocaleString('en-IN')}
                  </p>
                  <button
                    onClick={() => dispatch({ type: 'REMOVE_ITEM', payload: item.id })}
                    style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 12, cursor: 'pointer' }}
                  >Remove</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop summary sidebar */}
        {!isMobile && (
          <div style={{ width: 300, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 76 }}>
            <OrderSummary />
          </div>
        )}
      </div>
    </div>
  );
}
